import { useQuery } from '@tanstack/react-query';
import { AlertTriangleIcon, SlidersHorizontalIcon, XCircleIcon } from 'lucide-react';

import { api } from '@/lib/api';
import { useLabel, useT } from '@/lib/i18n';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Scoring Engine — the registered plugins, read live from `GET /api/health`.
 *
 * This was a hardcoded placeholder reading "No scoring plugins registered". Once
 * Phase 2 landed that sentence became false, which is the same failure as
 * inventing rows: a stakeholder-facing console stating something untrue. It now
 * reports what the API actually says, including saying nothing is registered
 * when nothing is.
 *
 * It is the *model* registry, not the per-module config surface: configs are
 * edited on Modules, against the module that owns them, because a config is
 * meaningless without the module's version and bank beside it.
 */

export function Scoring() {
  const t = useT();
  /**
   * The blurbs are keyed by model type (`scoring.model.RIASEC`) and looked up
   * through `useLabel`, which falls back to the raw value — here overridden to a
   * generic line, because a fourth plugin registering itself must not print
   * "IRT_BARS" as its own description. Adding a model still needs no pipeline
   * edit; it needs one catalogue line, in two files.
   */
  const label = useLabel();
  const { data, isLoading, error } = useQuery({
    queryKey: ['health'],
    queryFn: api.health,
    refetchInterval: 30_000,
  });

  const plugins = data?.plugins ?? [];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={t('nav.scoring')}
        description={t('scoring.description')}
        actions={
          data && (
            <Badge variant={plugins.length ? 'default' : 'destructive'}>
              {t('scoring.registered', { count: plugins.length })}
            </Badge>
          )
        }
      />

      {isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      )}

      {error && (
        <EmptyState
          icon={XCircleIcon}
          title={t('scoring.unreachable')}
          description={
            error instanceof Error ? error.message : t('scoring.unreachable.description')
          }
        />
      )}

      {data && plugins.length === 0 && (
        <EmptyState
          icon={AlertTriangleIcon}
          title={t('scoring.empty.title')}
          description={t('scoring.empty.description')}
        />
      )}

      {data && plugins.length > 0 && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {plugins.map((modelType) => (
              <Card key={modelType}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <SlidersHorizontalIcon className="size-4" />
                    <span className="font-mono" dir="ltr">{modelType}</span>
                  </CardTitle>
                  <CardDescription>
                    {label('scoring.model', modelType) === modelType
                      ? t('scoring.model.unknown')
                      : label('scoring.model', modelType)}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Badge variant="secondary">{t('scoring.registeredAtBoot')}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>


        </>
      )}
    </div>
  );
}
